import { Outlet } from '@tanstack/react-router';
import { useAtom } from 'jotai';
import styled from 'styled-components';

import { color } from '@creditornot/cb-ingredients/design-tokens';
import { Modal } from '@creditornot/cbt-modal';
import { NAVIGATION_MENU_WIDTH } from 'common/constants';
import { CONTENT_ROOT_ID } from 'common/hooks/useContentScroll';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { useShowStaticMenu } from 'common/hooks/useShowStaticMenu';
import { media } from 'common/media';
import { navigationModalOpenAtom } from 'common/state/navigationState';

import { NavigationMenu } from '../NavigationMenu';

const StaticMenuWithRoot = styled.div<{ $withoutMenu?: boolean }>`
  height: 100%;

  @media ${media.xl} {
    display: grid;
    ${({ $withoutMenu }) =>
      !$withoutMenu && `grid-template-columns: ${NAVIGATION_MENU_WIDTH}px 1fr;`}
  }
`;

const JumpToContentLink = styled.a`
  opacity: 0;
  height: 0;
  position: absolute;
  z-index: 2;
  padding: 8px;
  background: ${color.bg};
  color: ${color.textBrand};
  top: 0;
  left: 0;
  pointer-events: none;
  &:focus {
    opacity: 1;
    height: auto;
    pointer-events: auto;
  }
  display: none;
  @media ${media.xl} {
    display: block;
  }
`;

const Root = styled.main`
  display: flex;
  flex: 1;
  flex-direction: column;
  height: 100%;
  overflow: auto;
  position: relative;
  -webkit-overflow-scrolling: touch;
  z-index: 0;
`;

const StaticNavigationMenu = styled(NavigationMenu)<{ display?: 'initial' | 'none' }>`
  border-right: 1px solid ${color.border};
  display: none;

  @media ${media.xl} {
    display: ${({ display = 'initial' }) => display};
  }
`;

export default function MenuLayout() {
  const format = useFormatMessage();
  const showStaticMenu = useShowStaticMenu();

  const [modalOpen, setModalOpen] = useAtom(navigationModalOpenAtom);

  const handleDismiss = () => setModalOpen(false);

  return (
    <StaticMenuWithRoot $withoutMenu={!showStaticMenu}>
      <JumpToContentLink href={`#${CONTENT_ROOT_ID}`}>
        {format('app.jump_to_content')}
      </JumpToContentLink>
      <StaticNavigationMenu display={showStaticMenu ? 'initial' : 'none'} />
      <Root id={CONTENT_ROOT_ID} tabIndex={-1}>
        <Outlet />
        <Modal
          disableContentPadding
          onDismiss={handleDismiss}
          open={modalOpen}
          sizePreset="sideStartSheet:small"
        >
          <NavigationMenu onNavigate={handleDismiss} />
        </Modal>
      </Root>
    </StaticMenuWithRoot>
  );
}
